import React, { useCallback, useState } from 'react'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import { Route } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { OnboardingStepKey } from '@cbgms/api'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import { FormValues } from './types.d'
import { useDispatch } from 'react-redux'
import OnboardingWelcomeMessage from './WelcomeMessageForm'
import { updateShopSeenWelcomeScreenRequest } from '@cbgms/api/modules/shops/requests'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { useQueryClient } from '@tanstack/react-query'
import { OnboardingState } from 'app/auth/components/hooks/use-onboarding-setup'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'
import { ONBOARDING_CACHE_KEY } from 'app/auth/components/hooks/use-get-onboarding-step-data'

interface StepInputProps {
  basePath: string
}

interface StepProps {
  basePath: string
  onSubmit: (v: FormValues) => void
  onboardingInfo: OnboardingState
}

export const useWelcomeMessageStep: UseStepFunc<StepProps, StepInputProps> = ({ basePath, onboardingInfo }) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [isWelcomeMessageSeen, setIsWelcomeMessageSeen] = useState<boolean>(false)
  const stepName = OnboardingStepKey.WelcomeScreen
  const stepData = useStepData(stepName, onboardingInfo)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)

  const onStepSubmit = (values: FormValues) => {
    setIsWelcomeMessageSeen(values.IsWelcomeMessageSeen)
  }
  const complete = useCompleteOnSubmit(onStepSubmit)
  const onPersist = useCallback(async () => {
    if (isWelcomeMessageSeen) {
      await dispatch(updateShopSeenWelcomeScreenRequest(!isWelcomeMessageSeen)) // do call
      queryClient.invalidateQueries([ONBOARDING_CACHE_KEY])
    }
  }, [isWelcomeMessageSeen, dispatch])

  return {
    Component: WelcomeMessageStep,
    CTAComponent: null,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: false,
    showStepperHeader: false,
    hideSkip: true,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    stepProps: {
      duplicationCheck: stepData.DuplicationCheck || [],
      basePath,
      onSubmit: complete.onSubmit,
      onboardingInfo
    },
    persister: {
      onPersist,
      persistOrder: 3
    }
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.step_title_welcome_message')}</>
}

export const WelcomeMessageStep: React.FC<StepProps & defaultStepFormProps> = ({ basePath, ...rest }) => {
  return <Route path={routing.pathWelcomeMessageForm(basePath)} render={() => <OnboardingWelcomeMessage {...rest} />} />
}
